/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  Avatar,
  AvatarFallback,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  getInitials,
} from "@farmlands/shadcn-ui";
import { Mail, MessageCircleQuestionMark, User } from "lucide-react";
import { useSelector } from "react-redux";

import { TestIds } from "../../../testIds";
import { selectContactInfo } from "../../azure-b2c/storage";

const FALLBACK_EMAIL = "ask@farmlands.co.nz";
const HELP_URL = "https://www.farmlands.co.nz/support/contact";

const GetInTouchCardNoRepresentative = () => (
  <Card
    className="border-0 bg-grazing-green-100"
    data-testid={TestIds.feat.home.getInTouch.noRepComponent}
  >
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="text-lg font-bold text-verdure-forest-green">
        Have any questions?
      </CardTitle>
      <div className="flex size-10 items-center justify-center rounded bg-white">
        <MessageCircleQuestionMark aria-hidden />
      </div>
    </CardHeader>
    <CardContent className="flex flex-col gap-1">
      <span>
        Email us at{" "}
        <a
          href={`mailto:${FALLBACK_EMAIL}`}
          className="font-semibold text-verdure-forest-green underline"
          data-testid={TestIds.feat.home.getInTouch.noRepEmailLink}
        >
          {FALLBACK_EMAIL}
        </a>
        , or visit{" "}
        <a
          href={HELP_URL}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-verdure-forest-green underline"
          data-testid={TestIds.feat.home.getInTouch.noRepHelpLink}
        >
          Help
        </a>{" "}
        for more support options.
      </span>
    </CardContent>
  </Card>
);

export const GetInTouchCard = () => {
  const relationshipOwner = useSelector(selectContactInfo);
  const fullName = relationshipOwner.fullName;

  if (!fullName) {
    return <GetInTouchCardNoRepresentative />;
  }

  return (
    <Card
      className="border-0 bg-grazing-green-100"
      data-testid={TestIds.feat.home.getInTouch.component}
    >
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-bold text-verdure-forest-green">Get in touch!</CardTitle>
        <div className="flex size-10 items-center justify-center rounded bg-white">
          <User aria-hidden />
        </div>
      </CardHeader>
      <CardContent className="flex items-center gap-3">
        <Avatar className="size-11" data-testid={TestIds.feat.home.getInTouch.avatar}>
          <AvatarFallback className="bg-verdure-forest-green font-semibold text-white">
            {getInitials(fullName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <span className="font-semibold">{fullName}</span>
          <span className="text-2xs leading-xs tracking-[0.07px] text-neutral-800">
            Farmlands Representative
          </span>
        </div>
      </CardContent>
      <CardFooter>
        <Button asChild variant="outline" className="w-full">
          <a
            href={`mailto:${relationshipOwner.email ?? FALLBACK_EMAIL}`}
            data-testid={TestIds.feat.home.getInTouch.emailButton}
          >
            <Mail aria-hidden className="size-4" />
            Email
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
};
